import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import JSZip from 'jszip';
import './App.css';
import { PoliticaPrivacidad, PoliticaCookies, AvisoLegal } from './pages/LegalPages';
import AdminPanel from './pages/AdminPanel';
import CookieBanner from './components/CookieBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Iconos (Lucide-style SVGs) ─────────────────────────────────────────────

const Icon = ({ children, className = "" }) => (
  <svg className={`icon ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const ShieldIcon = ({ className }) => <Icon className={className}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></Icon>;
const UploadIcon = ({ className }) => <Icon className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></Icon>;
const DownloadIcon = ({ className }) => <Icon className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></Icon>;
const FileTextIcon = ({ className }) => <Icon className={className}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></Icon>;
const ZapIcon = ({ className }) => <Icon className={className}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></Icon>;
const CheckCircleIcon = ({ className }) => <Icon className={className}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></Icon>;
const AlertCircleIcon = ({ className }) => <Icon className={className}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></Icon>;
const AlertTriangleIcon = ({ className }) => <Icon className={className}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></Icon>;
const EyeIcon = ({ className }) => <Icon className={className}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></Icon>;
const EyeOffIcon = ({ className }) => <Icon className={className}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></Icon>;
const RotateCcwIcon = ({ className }) => <Icon className={className}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></Icon>;
const LockIcon = ({ className }) => <Icon className={className}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></Icon>;
const KeyIcon = ({ className }) => <Icon className={className}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></Icon>;
const CopyIcon = ({ className }) => <Icon className={className}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></Icon>;
const CheckIcon = ({ className }) => <Icon className={className}><polyline points="20 6 9 17 4 12"/></Icon>;
const XIcon = ({ className }) => <Icon className={className}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Icon>;
const ChevronDownIcon = ({ className }) => <Icon className={className}><polyline points="6 9 12 15 18 9"/></Icon>;
const ChevronUpIcon = ({ className }) => <Icon className={className}><polyline points="18 15 12 9 6 15"/></Icon>;
const ArrowRightIcon = ({ className }) => <Icon className={className}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></Icon>;
const RefreshCcwIcon = ({ className }) => <Icon className={className}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></Icon>;
const InfoIcon = ({ className }) => <Icon className={className}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></Icon>;
const MailIcon = ({ className }) => <Icon className={className}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></Icon>;
const ScaleIcon = ({ className }) => <Icon className={className}><line x1="12" y1="3" x2="12" y2="21"/><path d="M5 8L2 12l3 4"/><path d="M19 8l3 4-3 4"/><path d="M2 12h20"/></Icon>;
const CodeIcon = ({ className }) => <Icon className={className}><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></Icon>;
const ServerIcon = ({ className }) => <Icon className={className}><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></Icon>;
const FilesIcon = ({ className }) => <Icon className={className}><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 12a1 1 0 0 0-1 1v1a1 1 0 0 1-1 1 1 1 0 0 1 1 1v1a1 1 0 0 0 1 1"/><path d="M14 18a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1 1 1 0 0 1-1-1v-1a1 1 0 0 0-1-1"/></Icon>;
const PackageIcon = ({ className }) => <Icon className={className}><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></Icon>;
const LoaderIcon = ({ className }) => <Icon className={`${className} spin`}><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></Icon>;
const MapPinIcon = ({ className }) => <Icon className={className}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></Icon>;
const PhoneIcon = ({ className }) => <Icon className={className}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></Icon>;
const ExternalLinkIcon = ({ className }) => <Icon className={className}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></Icon>;

// ─── Constantes ─────────────────────────────────────────────────────────────

const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const STORAGE_KEY = "tecnoabogado_terminos_aceptados_v2";

const OPCIONES_DEFAULT = {
  dni_nie: true,
  nif_empresa: true,
  email: true,
  telefono: true,
  codigo_postal: true,
  iban: true,
  fecha: false,
  matricula: true,
  nombres: true,
  direcciones: true,
  num_expediente: false,
  num_autos: false,
};

const OPCIONES_LABELS = {
  dni_nie: "DNI / NIE",
  nif_empresa: "NIF Empresa",
  email: "Emails",
  telefono: "Teléfonos",
  codigo_postal: "Códigos Postales",
  iban: "IBAN / Cuentas",
  fecha: "Fechas",
  matricula: "Matrículas",
  nombres: "Nombres propios",
  direcciones: "Direcciones",
  num_expediente: "N.º Expediente",
  num_autos: "N.º Autos",
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function safeAtob(b64) {
  try {
    const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return arr;
  } catch {
    return null;
  }
}

// ─── CopyButton Component ───────────────────────────────────────────────────

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="copy-btn" title="Copiar">
      {copied ? <CheckIcon className="text-green" /> : <CopyIcon />}
    </button>
  );
}

// ─── DisclaimerBanner Component ─────────────────────────────────────────────

function DisclaimerBanner() {
  const [visible, setVisible] = useState(true);
  const [expanded, setExpanded] = useState(false);

  if (!visible) return null;

  return (
    <div className="disclaimer-banner">
      <div className="container">
        <div className="disclaimer-content">
          <AlertTriangleIcon className="text-amber" />
          <div className="disclaimer-text">
            <div className="disclaimer-header">
              <span className="disclaimer-title">Aviso legal importante</span>
              <span className="disclaimer-summary">Esta herramienta puede no detectar todos los datos personales.</span>
              <button onClick={() => setExpanded(!expanded)} className="disclaimer-toggle">
                {expanded ? <><ChevronUpIcon /> Menos</> : <><ChevronDownIcon /> Leer más</>}
              </button>
            </div>
            {expanded && (
              <p className="disclaimer-full">
                El resultado de esta herramienta es orientativo y no garantiza la anonimización completa ni el cumplimiento automático del Reglamento General de Protección de Datos (RGPD/GDPR), la Ley Orgánica 3/2018 (LOPDGDD) ni cualquier otra normativa aplicable. <strong>Es responsabilidad exclusiva de cada profesional revisar el documento resultante, verificar que no contiene datos personales identificables y asegurarse de que su uso cumple con la normativa de protección de datos vigente.</strong> Tecnoabogado no asume ninguna responsabilidad por omisiones en la detección ni por el uso que se haga de los documentos procesados.
              </p>
            )}
          </div>
          <button onClick={() => setVisible(false)} className="disclaimer-close">
            <XIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DisclaimerInline Component ─────────────────────────────────────────────

function DisclaimerInline() {
  return (
    <div className="disclaimer-inline">
      <AlertTriangleIcon className="text-amber" />
      <div>
        <span className="disclaimer-inline-title">Aviso importante: </span>
        ningún sistema automatizado garantiza la anonimización completa. Esta herramienta puede no detectar todos los datos personales presentes en el documento.{" "}
        <strong>
          Es responsabilidad exclusiva de cada profesional revisar el resultado, verificar la ausencia de datos identificables y garantizar el cumplimiento del RGPD, la LOPDGDD y la normativa de protección de datos aplicable en cada caso.
        </strong>{" "}
        Tecnoabogado no asume responsabilidad alguna por omisiones en la detección ni por el uso posterior de los documentos procesados.
      </div>
    </div>
  );
}

// ─── ModalAceptacion Component ──────────────────────────────────────────────

function ModalAceptacion({ visible, onAccept }) {
  const [paso, setPaso] = useState("email");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [checkedTerminos, setCheckedTerminos] = useState(false);
  const [checkedResponsabilidad, setCheckedResponsabilidad] = useState(false);
  const [checkedRGPD, setCheckedRGPD] = useState(false);

  const validarEmail = (v) => {
    if (!v.trim()) return "El email es obligatorio";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Introduce un email válido";
    return "";
  };

  const handleSiguiente = async () => {
    const err = validarEmail(email);
    if (err) { setEmailError(err); return; }
    setEmailError("");
    setEnviando(true);
    try {
      await fetch(`${API_URL}/api/registrar-acceso`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch (e) { console.error('Error registrando acceso:', e); }
    setEnviando(false);
    setPaso("terminos");
  };

  const todosAceptados = checkedTerminos && checkedResponsabilidad && checkedRGPD;

  const handleAceptar = () => {
    if (!todosAceptados) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ fecha: new Date().toISOString(), email }));
    onAccept();
  };

  if (!visible) return null;

  return (
    <>
      <div className="modal-overlay" />
      <div className="modal-container">
        <div className="modal-card">
          {/* Header */}
          <div className="modal-header">
            <div className="modal-header-icon">
              {paso === "email" ? <MailIcon className="text-primary" /> : <ScaleIcon className="text-amber" />}
            </div>
            <div>
              <p className="modal-label">{paso === "email" ? "Accede gratis" : "Antes de continuar"}</p>
              <h2 className="modal-title">
                {paso === "email" ? "Tecnoabogado Anonimizador" : "Aviso legal y condiciones de uso"}
              </h2>
            </div>
          </div>

          {/* Paso Email */}
          {paso === "email" && (
            <div className="modal-body">
              <p className="modal-desc">
                Introduce tu email profesional para acceder a la herramienta. Sin spam, solo actualizaciones importantes sobre la plataforma.
              </p>

              <div className={`input-group ${emailError ? 'error' : ''}`}>
                <MailIcon />
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); if (emailError) setEmailError(""); }}
                  onKeyDown={e => e.key === "Enter" && handleSiguiente()}
                  placeholder="tu@email.com"
                  autoFocus
                />
              </div>
              {emailError && <p className="input-error"><AlertCircleIcon /> {emailError}</p>}

              <p className="modal-rgpd">
                <LockIcon className="text-green" />
                Tus datos se tratan conforme al RGPD y la LOPDGDD. Responsable: José Luis Vilaplana - Tecnoabogado (Adriano 32-5º, Sevilla).
              </p>

              <div className="modal-indicators">
                <span><ShieldIcon className="text-primary" /> 100% gratuito</span>
                <span><LockIcon className="text-primary" /> Procesamiento seguro</span>
                <span><ScaleIcon className="text-primary" /> RGPD</span>
              </div>

              <button onClick={handleSiguiente} disabled={enviando} className="btn btn-primary btn-block">
                {enviando ? <><LoaderIcon /> Verificando...</> : <>Empezar a anonimizar <ArrowRightIcon /></>}
              </button>
            </div>
          )}

          {/* Paso Términos */}
          {paso === "terminos" && (
            <div className="modal-body">
              <p className="modal-desc">
                Esta herramienta aplica técnicas automáticas de detección y sustitución de datos personales mediante código y NLP clásico. Antes de usarla, debes leer y aceptar las siguientes condiciones:
              </p>

              <div className="checkbox-group">
                <label className="checkbox-item" onClick={() => setCheckedTerminos(!checkedTerminos)}>
                  <div className={`checkbox ${checkedTerminos ? 'checked' : ''}`}>
                    {checkedTerminos && <CheckIcon />}
                  </div>
                  <div>
                    <div className="checkbox-title"><AlertTriangleIcon className="text-amber" /> Limitaciones técnicas</div>
                    <p className="checkbox-desc">
                      Entiendo que <strong>ningún sistema automatizado garantiza la anonimización completa</strong>. Esta herramienta puede no detectar todos los datos personales presentes en el documento.
                    </p>
                  </div>
                </label>

                <label className="checkbox-item" onClick={() => setCheckedResponsabilidad(!checkedResponsabilidad)}>
                  <div className={`checkbox ${checkedResponsabilidad ? 'checked' : ''}`}>
                    {checkedResponsabilidad && <CheckIcon />}
                  </div>
                  <div>
                    <div className="checkbox-title"><ScaleIcon className="text-primary" /> Responsabilidad profesional</div>
                    <p className="checkbox-desc">
                      Asumo que es <strong>mi responsabilidad exclusiva como profesional</strong> revisar el documento resultante y verificar que no contiene datos personales identificables.
                    </p>
                  </div>
                </label>

                <label className="checkbox-item" onClick={() => setCheckedRGPD(!checkedRGPD)}>
                  <div className={`checkbox ${checkedRGPD ? 'checked' : ''}`}>
                    {checkedRGPD && <CheckIcon />}
                  </div>
                  <div>
                    <div className="checkbox-title"><LockIcon className="text-green" /> Normativa de protección de datos</div>
                    <p className="checkbox-desc">
                      Conozco y me comprometo a cumplir el <strong>RGPD (UE 2016/679)</strong>, la <strong>LOPDGDD (LO 3/2018)</strong> y cualquier otra normativa de protección de datos aplicable.
                    </p>
                  </div>
                </label>
              </div>

              <button onClick={handleAceptar} disabled={!todosAceptados} className={`btn btn-block ${todosAceptados ? 'btn-primary' : 'btn-disabled'}`}>
                {todosAceptados ? <><CheckCircleIcon /> He leído y acepto — Continuar</> : <><ScaleIcon /> Marca las tres casillas para continuar</>}
              </button>
              <p className="modal-note">Esta aceptación se guarda en tu navegador.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Header Component ───────────────────────────────────────────────────────

function Header() {
  return (
    <header className="header">
      <div className="container header-content">
        <a href="/" className="logo">
          <div className="logo-icon">
            <ShieldIcon className="text-primary" />
            <div className="logo-dot" />
          </div>
          <div className="logo-text">
            <span className="logo-brand">Tecno<span className="text-primary">abogado</span></span>
            <span className="logo-sub">Anonimizador</span>
          </div>
        </a>

        <nav className="nav">
          <a href="#herramienta">Herramienta</a>
          <a href="#como-funciona">Cómo funciona</a>
          <a href="#privacidad">Privacidad</a>
          <a href="https://vilaplanatecnoabogado.es/" target="_blank" rel="noopener noreferrer" className="btn btn-outline">
            Tecnoabogado.com
          </a>
        </nav>
      </div>
    </header>
  );
}

// ─── Footer Component ───────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <div className="logo">
              <div className="logo-icon small">
                <ShieldIcon className="text-primary" />
              </div>
              <span className="logo-brand">Tecno<span className="text-primary">abogado</span></span>
            </div>
            <p>Herramienta profesional de anonimización de documentos legales para despachos de abogados. Sin IA, sin envío de datos a terceros.</p>
            <div className="footer-status">
              <div className="status-dot" />
              <span>Procesamiento seguro en servidor</span>
            </div>
          </div>

          <div className="footer-links">
            <h4>Legal</h4>
            <ul>
              <li><Link to="/aviso-legal">Aviso Legal</Link></li>
              <li><Link to="/politica-privacidad">Política de Privacidad</Link></li>
              <li><Link to="/politica-cookies">Política de Cookies</Link></li>
            </ul>
          </div>

          <div className="footer-contact">
            <h4>Contacto</h4>
            <ul>
              <li><MapPinIcon className="text-primary" /> Adriano 32-5º, 41001 Sevilla</li>
              <li><PhoneIcon className="text-primary" /> <a href="tel:954564602">954 564 602</a></li>
              <li><MailIcon className="text-primary" /> <a href="mailto:info@tecnoabogado.com">info@tecnoabogado.com</a></li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} Tecnoabogado. Todos los derechos reservados.</p>
          <p>Herramienta desarrollada para el cumplimiento del <span className="text-primary">RGPD</span> en despachos jurídicos</p>
        </div>
      </div>
    </footer>
  );
}

// ─── HomePage Component ─────────────────────────────────────────────────────

function HomePage() {
  // Modal state - check localStorage on initial render
  const [showModal, setShowModal] = useState(() => !localStorage.getItem(STORAGE_KEY));

  // Tool state
  const [tabActivo, setTabActivo] = useState("anonimizar");
  const [tipoActivo, setTipoActivo] = useState("texto");
  const [texto, setTexto] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [opciones, setOpciones] = useState(OPCIONES_DEFAULT);
  const [modoRecuperable, setModoRecuperable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [mostrarOriginal, setMostrarOriginal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Batch state
  const [lote, setLote] = useState([]);
  const [modoLote, setModoLote] = useState(false);
  const [procesandoLote, setProcesandoLote] = useState(false);
  const [progresoLote, setProgresoLote] = useState(0);

  // Recovery state
  const [textoAnonimizado, setTextoAnonimizado] = useState("");
  const [mapaCifrado, setMapaCifrado] = useState("");
  const [claveRecuperacion, setClaveRecuperacion] = useState("");
  const [textoRecuperado, setTextoRecuperado] = useState("");
  const [recuperando, setRecuperando] = useState(false);

  const handleModalAccept = () => {
    setShowModal(false);
  };

  // File handling
  const validarArchivo = useCallback((file) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    const allowed = ["docx", "pdf", "jpg", "jpeg", "png", "gif", "bmp", "tiff", "webp"];
    if (!ext || !allowed.includes(ext)) return "Formato no soportado. Usa: DOCX, PDF o imágenes.";
    if (file.size > MAX_FILE_SIZE_BYTES) return `El archivo supera ${MAX_FILE_SIZE_MB} MB.`;
    return null;
  }, []);

  const handleFileSelect = useCallback((file) => {
    const err = validarArchivo(file);
    if (err) {
      toast.error(err);
      return;
    }
    setArchivo(file);
    setResultado(null);
    setModoLote(false);
    setLote([]);
    setTipoActivo("archivo");
  }, [validarArchivo]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    if (files.length === 1) {
      handleFileSelect(files[0]);
    } else {
      // Multiple files: batch mode
      const validos = [];
      const errores = [];
      for (const f of files) {
        const err = validarArchivo(f);
        if (err) errores.push(`${f.name}: ${err}`);
        else validos.push(f);
      }
      if (errores.length > 0) toast.error(`${errores.length} archivo(s) rechazado(s)`);
      if (validos.length > 0) {
        const items = validos.map(f => ({
          id: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
          archivo: f,
          estado: "pendiente",
          progreso: 0,
        }));
        setLote(items);
        setModoLote(true);
        setTipoActivo("archivo");
        toast.success(`${validos.length} archivo(s) listos para procesar`);
      }
    }
  }, [validarArchivo, handleFileSelect]);

  const getTipoArchivo = (file) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext === "docx") return "docx";
    if (ext === "pdf") return "pdf";
    return "imagen";
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const result = reader.result;
          if (result && typeof result === 'string') {
            const base64 = result.split(",")[1];
            if (base64) {
              resolve(base64);
            } else {
              reject(new Error("No se pudo extraer el contenido del archivo"));
            }
          } else {
            reject(new Error("Error leyendo el archivo"));
          }
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Error al leer el archivo"));
      reader.readAsDataURL(file);
    });
  };

  const toggleOpcion = (key) => {
    setOpciones(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Process text/document
  const handleProcesar = async () => {
    setResultado(null);
    setIsLoading(true);

    try {
      if (tipoActivo === "texto") {
        if (!texto.trim()) {
          toast.error("Por favor, introduce texto para anonimizar");
          setIsLoading(false);
          return;
        }
        const res = await fetch(`${API_URL}/api/anonimizar/texto`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ texto, opciones, modo_recuperable: modoRecuperable }),
        });
        const data = await res.json();
        if (data.success) {
          setResultado(data);
          toast.success(`Anonimización completada: ${data.estadisticas?.total ?? 0} datos detectados`);
        } else {
          toast.error(data.error || "Error al procesar el texto");
        }
      } else {
        if (!archivo) {
          toast.error("Por favor, selecciona un archivo");
          setIsLoading(false);
          return;
        }
        
        toast.loading("Procesando documento...", { id: "processing" });
        
        const contenido = await fileToBase64(archivo);
        const tipo = getTipoArchivo(archivo);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minutos timeout
        
        try {
          const res = await fetch(`${API_URL}/api/anonimizar/documento`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              contenido,
              tipo,
              nombre_archivo: archivo.name,
              opciones,
              modo_recuperable: modoRecuperable,
            }),
            signal: controller.signal,
          });
          
          clearTimeout(timeoutId);
          
          const data = await res.json();
          toast.dismiss("processing");
          
          if (data.success) {
            setResultado(data);
            toast.success(`Anonimización completada: ${data.estadisticas?.total ?? 0} datos detectados`);
          } else {
            toast.error(data.error || "Error al procesar el archivo");
          }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          toast.dismiss("processing");
          if (fetchError.name === 'AbortError') {
            toast.error("El archivo es muy grande. Tiempo de espera agotado.");
          } else {
            throw fetchError;
          }
        }
      }
    } catch (e) {
      console.error("Error procesando:", e);
      toast.dismiss("processing");
      if (e.name === 'AbortError') {
        toast.error("Tiempo de espera agotado. Intenta con un archivo más pequeño.");
      } else if (e.message) {
        toast.error(`Error: ${e.message}`);
      } else {
        toast.error("Error al conectar con el servidor. Revisa la consola para más detalles.");
      }
    }
    setIsLoading(false);
  };

  // Batch processing
  const handleProcesarLote = async () => {
    if (lote.length === 0) return;
    setProcesandoLote(true);
    setProgresoLote(0);
    let completados = 0;

    for (const item of lote) {
      setLote(prev => prev.map(i => i.id === item.id ? { ...i, estado: "procesando", progreso: 10 } : i));
      try {
        const contenido = await fileToBase64(item.archivo);
        const tipo = getTipoArchivo(item.archivo);
        setLote(prev => prev.map(i => i.id === item.id ? { ...i, progreso: 40 } : i));
        const res = await fetch(`${API_URL}/api/anonimizar/documento`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contenido,
            tipo,
            nombre_archivo: item.archivo.name,
            opciones,
            modo_recuperable: false,
          }),
        });
        const data = await res.json();
        setLote(prev => prev.map(i => i.id === item.id ? {
          ...i,
          estado: data.success ? "ok" : "error",
          progreso: 100,
          resultado: data.success ? {
            contenido: data.contenido,
            texto_anonimizado: data.texto_anonimizado,
            estadisticas: data.estadisticas,
          } : { error: data.error || "Error desconocido" },
        } : i));
      } catch (e) {
        setLote(prev => prev.map(i => i.id === item.id ? { ...i, estado: "error", progreso: 100, resultado: { error: "Error de conexión" } } : i));
      }
      completados++;
      setProgresoLote(Math.round((completados / lote.length) * 100));
    }
    setProcesandoLote(false);
    toast.success(`Lote completado: ${completados} archivo(s) procesados`);
  };

  const handleDescargarZip = async () => {
    const procesados = lote.filter(i => i.estado === "ok" && i.resultado);
    if (procesados.length === 0) { 
      toast.error("No hay archivos procesados"); 
      return; 
    }
    
    const toastId = toast.loading("Generando ZIP...");
    
    try {
      const zip = new JSZip();
      for (const item of procesados) {
        const r = item.resultado;
        const nombreBase = `anonimizado_${item.archivo.name}`;
        if (r.contenido) {
          const arr = safeAtob(r.contenido);
          if (arr) zip.file(nombreBase.replace(/\.[^.]+$/, ".txt"), arr);
        } else if (r.texto_anonimizado) {
          zip.file(`${nombreBase}.txt`, r.texto_anonimizado);
        }
      }
      
      const blob = await zip.generateAsync({ type: "blob" });
      
      // Método más robusto de descarga
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      link.download = `tecnoabogado_anonimizados_${new Date().toISOString().split("T")[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      
      // Limpiar después de un pequeño delay
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(`ZIP descargado: ${procesados.length} archivo(s)`, { id: toastId });
    } catch (error) {
      console.error("Error generando ZIP:", error);
      toast.error("Error al generar el ZIP", { id: toastId });
    }
  };

  // Download result in different formats
  const handleDescargarFormato = async (formato) => {
    if (!resultado?.texto_anonimizado) {
      toast.error("No hay texto para descargar");
      return;
    }
    
    const toastId = toast.loading(`Generando ${formato.toUpperCase()}...`);
    
    try {
      const res = await fetch(`${API_URL}/api/generar-formato`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          texto: resultado.texto_anonimizado,
          formato: formato,
          nombre_archivo: archivo ? archivo.name.replace(/\.[^.]+$/, "") : "documento_anonimizado",
          estadisticas: resultado.estadisticas,
        }),
      });
      const data = await res.json();
      
      if (data.success) {
        const arr = safeAtob(data.contenido);
        if (!arr) { 
          toast.error("Error al decodificar el archivo", { id: toastId }); 
          return; 
        }
        
        const blob = new Blob([arr], { type: data.mime_type || "application/octet-stream" });
        
        // Método alternativo: usar window.navigator.msSaveOrOpenBlob para IE/Edge
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, data.nombre_archivo);
          toast.success(`${formato.toUpperCase()} descargado: ${data.nombre_archivo}`, { id: toastId });
          return;
        }
        
        // Método estándar con iframe oculto para forzar descarga
        const url = URL.createObjectURL(blob);
        
        // Crear iframe oculto para forzar la descarga
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
        
        const link = document.createElement("a");
        link.href = url;
        link.download = data.nombre_archivo;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        
        // Añadir al DOM y hacer click
        document.body.appendChild(link);
        
        // Forzar click con timeout
        setTimeout(() => {
          link.click();
          
          // Limpiar después
          setTimeout(() => {
            document.body.removeChild(link);
            document.body.removeChild(iframe);
            URL.revokeObjectURL(url);
          }, 1000);
        }, 100);
        
        toast.success(`${formato.toUpperCase()} descargado: ${data.nombre_archivo}`, { id: toastId });
      } else {
        toast.error(data.error || "Error al generar", { id: toastId });
      }
    } catch (e) {
      console.error("Error descargando:", e);
      toast.error("Error de conexión", { id: toastId });
    }
  };

  // Legacy download (for backwards compatibility)
  const handleDescargar = () => {
    handleDescargarFormato("txt");
  };

  const handleDescargarMapa = () => {
    if (!resultado?.mapa_cifrado || !resultado?.clave_recuperacion) return;
    const contenido = JSON.stringify({
      mapa_cifrado: resultado.mapa_cifrado,
      clave_recuperacion: resultado.clave_recuperacion,
      fecha: new Date().toISOString(),
      nota: "Guarda este archivo en un lugar seguro. Es necesario para recuperar los datos originales.",
    }, null, 2);
    const blob = new Blob([contenido], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clave_recuperacion_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setTexto("");
    setArchivo(null);
    setResultado(null);
    setMostrarOriginal(false);
  };

  // Recovery
  const handleRecuperar = async () => {
    if (!textoAnonimizado.trim() || !mapaCifrado.trim() || !claveRecuperacion.trim()) {
      toast.error("Rellena todos los campos");
      return;
    }
    setRecuperando(true);
    try {
      const res = await fetch(`${API_URL}/api/anonimizar/recuperar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          texto_anonimizado: textoAnonimizado,
          mapa_cifrado: mapaCifrado,
          clave: claveRecuperacion,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setTextoRecuperado(data.texto_recuperado);
        toast.success(`Datos recuperados: ${data.sustituciones_recuperadas} sustituciones restauradas`);
      } else {
        toast.error(data.error || "Error al recuperar los datos");
      }
    } catch (e) {
      toast.error("Error de conexión");
    }
    setRecuperando(false);
  };

  const toastConfig = useMemo(() => ({
    style: { background: '#1a1a1a', color: '#fff', border: '1px solid #333' }
  }), []);

  const detectionTags = useMemo(() => {
    if (!resultado?.estadisticas?.detecciones) return [];
    return Object.entries(resultado.estadisticas.detecciones).filter(([, v]) => v > 0);
  }, [resultado]);

  return (
    <div className="app">
      <Toaster position="top-right" toastOptions={toastConfig} />
      <ModalAceptacion visible={showModal} onAccept={handleModalAccept} />
      
      <Header />
      <DisclaimerBanner />

      <main>
        {/* Hero */}
        <section className="hero">
          <div className="hero-bg">
            <div className="hero-glow" />
            <div className="hero-glow-2" />
          </div>
          <div className="container hero-content">
            <div className="hero-badge">
              <div className="badge-dot" />
              Sin IA · Solo código · RGPD · Procesamiento seguro
            </div>
            <h1>
              Anonimiza tus <span className="text-primary">documentos legales</span> de forma segura
            </h1>
            <p>
              Detecta y elimina automáticamente nombres, DNI, direcciones, teléfonos y más datos personales de tus escritos judiciales, contratos y documentación de clientes.
            </p>
            <div className="hero-features">
              <span><LockIcon className="text-primary" /> Sin envío de datos a terceros</span>
              <span><CodeIcon className="text-primary" /> Procesamiento por código, sin IA</span>
              <span><ServerIcon className="text-primary" /> Servidor seguro en España</span>
              <span><KeyIcon className="text-primary" /> Recuperación cifrada AES</span>
            </div>
            <a href="#herramienta" className="hero-scroll">
              <span>Usar herramienta</span>
              <ChevronDownIcon />
            </a>
          </div>
        </section>

        {/* Tool Section */}
        <section id="herramienta" className="tool-section">
          <div className="container">
            <div className="tool-wrapper">
              
              {/* Main Tabs */}
              <div className="main-tabs">
                <button
                  onClick={() => setTabActivo("anonimizar")}
                  className={`tab ${tabActivo === "anonimizar" ? "active" : ""}`}
                  data-testid="tab-anonimizar"
                >
                  <ShieldIcon /> Anonimizar
                </button>
                <button
                  onClick={() => setTabActivo("recuperar")}
                  className={`tab ${tabActivo === "recuperar" ? "active" : ""}`}
                  data-testid="tab-recuperar"
                >
                  <RefreshCcwIcon /> Recuperar datos
                </button>
              </div>

              {/* Anonimizar Panel */}
              {tabActivo === "anonimizar" && (
                <div className="tool-grid">
                  {/* Left Panel: Options */}
                  <div className="options-panel">
                    <div className="card">
                      <h3><ShieldIcon className="text-primary" /> Datos a anonimizar</h3>
                      <div className="options-list">
                        {Object.keys(OPCIONES_LABELS).map(key => (
                          <label key={key} className="option-item" onClick={() => toggleOpcion(key)}>
                            <div className={`checkbox ${opciones[key] ? 'checked' : ''}`}>
                              {opciones[key] && <CheckIcon />}
                            </div>
                            <span className={opciones[key] ? 'active' : ''}>{OPCIONES_LABELS[key]}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Recovery Mode */}
                    <div className={`card recoverable-card ${modoRecuperable ? 'active' : ''}`}>
                      <label className="recoverable-toggle" onClick={() => setModoRecuperable(!modoRecuperable)}>
                        <div className={`checkbox ${modoRecuperable ? 'checked' : ''}`}>
                          {modoRecuperable && <CheckIcon />}
                        </div>
                        <div>
                          <div className="recoverable-title"><KeyIcon className="text-primary" /> Modo recuperable</div>
                          <p>Genera una clave cifrada (AES) para poder recuperar los datos originales.</p>
                        </div>
                      </label>
                      {modoRecuperable && (
                        <div className="recoverable-warning">
                          <InfoIcon className="text-primary" />
                          <p>Guarda la clave de recuperación en un lugar seguro.</p>
                        </div>
                      )}
                    </div>

                    {/* Supported Formats */}
                    <div className="card">
                      <h3>Formatos soportados</h3>
                      <div className="formats-list">
                        <span><FileTextIcon className="text-blue" /> Word (.docx)</span>
                        <span><FileTextIcon className="text-red" /> PDF (.pdf)</span>
                        <span><FileTextIcon className="text-primary" /> Texto directo</span>
                      </div>
                    </div>
                  </div>

                  {/* Right Panel: Input/Output */}
                  <div className="main-panel">
                    {/* Input Type Tabs */}
                    <div className="input-tabs">
                      <button
                        onClick={() => setTipoActivo("texto")}
                        className={`tab ${tipoActivo === "texto" ? "active" : ""}`}
                        data-testid="tab-texto"
                      >
                        Texto directo
                      </button>
                      <button
                        onClick={() => setTipoActivo("archivo")}
                        className={`tab ${tipoActivo === "archivo" ? "active" : ""}`}
                        data-testid="tab-archivo"
                      >
                        Subir archivo
                      </button>
                    </div>

                    {/* Text Input */}
                    {tipoActivo === "texto" && (
                      <div className="text-input-wrapper">
                        <textarea
                          value={texto}
                          onChange={e => setTexto(e.target.value)}
                          placeholder="Pega aquí el texto del documento legal que deseas anonimizar...

Ejemplo: El cliente Juan García López, con DNI 12345678Z, domiciliado en Calle Mayor 15, 28001 Madrid, teléfono 612345678..."
                          className="text-input"
                          data-testid="texto-input"
                        />
                        <div className="text-input-footer">
                          <span>{texto.length.toLocaleString()} caracteres</span>
                          {texto && <button onClick={() => setTexto("")}>Limpiar</button>}
                        </div>
                      </div>
                    )}

                    {/* File Input */}
                    {tipoActivo === "archivo" && (
                      <div className="file-input-wrapper">
                        <div
                          onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                          onDragLeave={() => setIsDragging(false)}
                          onDrop={handleDrop}
                          onClick={() => { if (!modoLote) fileInputRef.current?.click(); }}
                          className={`drop-zone ${isDragging ? 'dragging' : ''} ${archivo ? 'has-file' : ''} ${modoLote ? 'batch-mode' : ''}`}
                          data-testid="drop-zone"
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".docx,.pdf,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp"
                            multiple
                            onChange={e => {
                              const files = e.target.files;
                              if (!files || files.length === 0) return;
                              if (files.length === 1) handleFileSelect(files[0]);
                              else {
                                const items = Array.from(files).filter(f => !validarArchivo(f)).map(f => ({
                                  id: `${f.name}-${Date.now()}`,
                                  archivo: f,
                                  estado: "pendiente",
                                  progreso: 0,
                                }));
                                setLote(items);
                                setModoLote(true);
                              }
                              e.target.value = "";
                            }}
                          />

                          {modoLote ? (
                            <div className="batch-list">
                              {lote.map(item => (
                                <div key={item.id} className="batch-item">
                                  <FileTextIcon className="text-primary" />
                                  <div className="batch-item-info">
                                    <p className="batch-item-name">{item.archivo.name}</p>
                                    <div className="batch-item-status">
                                      <span>{(item.archivo.size / 1024).toFixed(0)} KB</span>
                                      {item.estado === "procesando" && <div className="progress-bar"><div style={{ width: `${item.progreso}%` }} /></div>}
                                      {item.estado === "ok" && <CheckCircleIcon className="text-green" />}
                                      {item.estado === "error" && <span className="text-red" title={item.resultado?.error}><AlertCircleIcon /> Error</span>}
                                    </div>
                                  </div>
                                  {item.estado === "pendiente" && (
                                    <button onClick={e => { e.stopPropagation(); setLote(prev => prev.filter(i => i.id !== item.id)); }} className="batch-remove">
                                      <XIcon />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : archivo ? (
                            <div className="file-selected">
                              <div className="file-icon"><FileTextIcon className="text-primary" /></div>
                              <p className="file-name">{archivo.name}</p>
                              <p className="file-size">{(archivo.size / 1024 / 1024).toFixed(2)} MB</p>
                              <button onClick={e => { e.stopPropagation(); setArchivo(null); setResultado(null); }}>Cambiar archivo</button>
                            </div>
                          ) : (
                            <div className="drop-zone-empty">
                              <div className="drop-icon"><UploadIcon /></div>
                              <p className="drop-title">Arrastra uno o varios documentos aquí</p>
                              <p className="drop-subtitle">o haz clic para seleccionar · DOCX, PDF</p>
                              <span className="drop-limit">Máx. {MAX_FILE_SIZE_MB} MB por archivo</span>
                            </div>
                          )}
                        </div>

                        {/* Batch Progress */}
                        {procesandoLote && (
                          <div className="batch-progress">
                            <div className="batch-progress-header">
                              <span>Procesando lote...</span>
                              <span>{progresoLote}%</span>
                            </div>
                            <div className="progress-bar"><div style={{ width: `${progresoLote}%` }} /></div>
                          </div>
                        )}

                        {/* Batch Actions */}
                        {modoLote && (
                          <div className="batch-actions">
                            <button onClick={() => fileInputRef.current?.click()} className="btn btn-outline btn-sm">
                              <FilesIcon /> Añadir más
                            </button>
                            <button onClick={() => { setLote([]); setModoLote(false); }} className="btn btn-outline btn-sm">
                              <RotateCcwIcon /> Limpiar lote
                            </button>
                            {lote.some(i => i.estado === "ok") && (
                              <button onClick={handleDescargarZip} className="btn btn-link">
                                <PackageIcon /> Descargar ZIP ({lote.filter(i => i.estado === "ok").length})
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Process Button */}
                    {modoLote ? (
                      <button
                        onClick={handleProcesarLote}
                        disabled={procesandoLote || lote.length === 0 || lote.every(i => i.estado !== "pendiente")}
                        className="btn btn-primary btn-block btn-lg"
                        data-testid="btn-procesar-lote"
                      >
                        {procesandoLote ? (
                          <><LoaderIcon /> Procesando lote ({lote.filter(i => i.estado !== "pendiente").length}/{lote.length})...</>
                        ) : (
                          <><FilesIcon /> Anonimizar {lote.filter(i => i.estado === "pendiente").length} archivo(s) en lote <ArrowRightIcon /></>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={handleProcesar}
                        disabled={isLoading || (tipoActivo === "texto" ? !texto.trim() : !archivo)}
                        className="btn btn-primary btn-block btn-lg"
                        data-testid="btn-procesar"
                      >
                        {isLoading ? (
                          <><LoaderIcon /> Procesando documento...</>
                        ) : (
                          <><ZapIcon /> Anonimizar ahora <ArrowRightIcon /></>
                        )}
                      </button>
                    )}

                    {/* Disclaimer */}
                    <DisclaimerInline />

                    {/* Result */}
                    {resultado && (
                      <div className="result-panel" data-testid="resultado-panel">
                        {/* Stats */}
                        {resultado.estadisticas && (
                          <div className={`result-stats ${resultado.estadisticas.total === 0 ? 'warning' : 'success'}`}>
                            <div className="result-stats-header">
                              <div className="result-stats-title">
                                {resultado.estadisticas.total === 0 ? (
                                  <><AlertCircleIcon className="text-amber" /> Sin datos personales detectados</>
                                ) : (
                                  <><CheckCircleIcon className="text-green" /> {resultado.estadisticas.total} dato(s) anonimizado(s)</>
                                )}
                              </div>
                              <div className="result-stats-actions">
                                {resultado.texto_anonimizado && (
                                  <button onClick={() => setMostrarOriginal(!mostrarOriginal)}>
                                    {mostrarOriginal ? <><EyeOffIcon /> Ver anonimizado</> : <><EyeIcon /> Ver original</>}
                                  </button>
                                )}
                                <button onClick={handleReset}><RotateCcwIcon /> Nuevo</button>
                              </div>
                            </div>
                            {resultado.estadisticas.total > 0 && (
                              <div className="result-tags">
                                {detectionTags.map(([k, v]) => (
                                    <span key={k} className="result-tag">{OPCIONES_LABELS[k] || k}: {v}</span>
                                  ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Note */}
                        {resultado.nota && (
                          <div className="result-note">
                            <AlertCircleIcon className="text-primary" />
                            {resultado.nota}
                          </div>
                        )}

                        {/* Text Result */}
                        {resultado.texto_anonimizado && (
                          <div className="result-text-card">
                            <div className="result-text-header">
                              {mostrarOriginal ? "Texto original" : "Texto anonimizado"}
                            </div>
                            <pre className="result-text" data-testid="resultado-texto">
                              {mostrarOriginal ? resultado.texto_original : resultado.texto_anonimizado}
                            </pre>
                          </div>
                        )}

                        {/* Recovery Key */}
                        {resultado.mapa_cifrado && resultado.clave_recuperacion && (
                          <div className="recovery-key-card">
                            <div className="recovery-key-header">
                              <KeyIcon className="text-primary" />
                              <span>Clave de recuperación generada</span>
                            </div>
                            <div className="recovery-key-fields">
                              <div className="recovery-field">
                                <div className="recovery-field-header">
                                  <span>Clave AES (guárdala en lugar seguro)</span>
                                  <CopyButton text={resultado.clave_recuperacion} />
                                </div>
                                <div className="recovery-field-value">{resultado.clave_recuperacion}</div>
                              </div>
                              <div className="recovery-field">
                                <div className="recovery-field-header">
                                  <span>Mapa cifrado</span>
                                  <CopyButton text={resultado.mapa_cifrado} />
                                </div>
                                <div className="recovery-field-value truncate">{resultado.mapa_cifrado.substring(0, 80)}...</div>
                              </div>
                            </div>
                            <button onClick={handleDescargarMapa} className="btn btn-outline btn-block">
                              <DownloadIcon /> Descargar clave de recuperación (.json)
                            </button>
                          </div>
                        )}

                        {/* Download Buttons */}
                        <div className="download-formats">
                          <p className="download-label">Descargar documento anonimizado:</p>
                          <div className="download-buttons">
                            <button onClick={() => handleDescargarFormato("pdf")} className="btn btn-download" data-testid="btn-descargar-pdf">
                              <FileTextIcon className="text-red" /> PDF
                            </button>
                            <button onClick={() => handleDescargarFormato("docx")} className="btn btn-download" data-testid="btn-descargar-docx">
                              <FileTextIcon className="text-blue" /> DOCX
                            </button>
                            <button onClick={() => handleDescargarFormato("markdown")} className="btn btn-download" data-testid="btn-descargar-md">
                              <CodeIcon className="text-green" /> Markdown
                            </button>
                            <button onClick={() => handleDescargarFormato("txt")} className="btn btn-download" data-testid="btn-descargar-txt">
                              <FileTextIcon className="text-primary" /> TXT
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recuperar Panel */}
              {tabActivo === "recuperar" && (
                <div className="recovery-panel">
                  <div className="recovery-info">
                    <InfoIcon className="text-primary" />
                    <div>
                      <p className="recovery-info-title">Recuperación de datos originales</p>
                      <p>Para recuperar los datos originales necesitas el <strong>texto anonimizado</strong>, el <strong>mapa cifrado</strong> y la <strong>clave de recuperación</strong> que se generaron al anonimizar con el modo recuperable activado.</p>
                    </div>
                  </div>

                  <div className="card recovery-form">
                    <div className="form-group">
                      <label>Texto anonimizado</label>
                      <textarea
                        value={textoAnonimizado}
                        onChange={e => setTextoAnonimizado(e.target.value)}
                        placeholder="Pega aquí el texto anonimizado (con los tokens [NOMBRE_001], [DNI-NIE_001], etc.)..."
                        className="form-textarea"
                        data-testid="recuperar-texto"
                      />
                    </div>

                    <div className="form-group">
                      <label>Mapa cifrado</label>
                      <textarea
                        value={mapaCifrado}
                        onChange={e => setMapaCifrado(e.target.value)}
                        placeholder="Pega aquí el mapa cifrado (campo mapa_cifrado del archivo .json)..."
                        className="form-textarea small"
                        data-testid="recuperar-mapa"
                      />
                    </div>

                    <div className="form-group">
                      <label>Clave de recuperación</label>
                      <input
                        type="text"
                        value={claveRecuperacion}
                        onChange={e => setClaveRecuperacion(e.target.value)}
                        placeholder="Pega aquí la clave de recuperación..."
                        className="form-input"
                        data-testid="recuperar-clave"
                      />
                    </div>

                    <button
                      onClick={handleRecuperar}
                      disabled={recuperando || !textoAnonimizado.trim() || !mapaCifrado.trim() || !claveRecuperacion.trim()}
                      className="btn btn-primary btn-block"
                      data-testid="btn-recuperar"
                    >
                      {recuperando ? <><LoaderIcon /> Recuperando datos...</> : <><RefreshCcwIcon /> Recuperar datos originales</>}
                    </button>
                  </div>

                  {/* Recovery Result */}
                  {textoRecuperado && (
                    <div className="recovery-result">
                      <div className="recovery-result-header">
                        <CheckCircleIcon className="text-green" />
                        <span>Datos recuperados correctamente</span>
                      </div>
                      <pre className="recovery-result-text" data-testid="texto-recuperado">{textoRecuperado}</pre>
                      <button
                        onClick={() => {
                          const blob = new Blob([textoRecuperado], { type: "text/plain;charset=utf-8" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = "documento_recuperado.txt";
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="btn btn-secondary btn-block"
                      >
                        <DownloadIcon className="text-primary" /> Descargar documento recuperado
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="como-funciona" className="how-section">
          <div className="container">
            <div className="how-header">
              <h2>Cómo funciona</h2>
              <p>Un proceso simple y seguro en tres pasos. Sin IA, sin aprendizaje automático, solo código determinista.</p>
            </div>

            <div className="how-steps">
              <div className="how-step">
                <div className="step-number">01</div>
                <div className="step-icon"><UploadIcon className="text-primary" /></div>
                <h3>Sube tu documento</h3>
                <p>Arrastra o selecciona tu archivo Word, PDF. También puedes pegar el texto directamente.</p>
              </div>

              <div className="how-step">
                <div className="step-number">02</div>
                <div className="step-icon"><ShieldIcon className="text-primary" /></div>
                <h3>Selecciona qué anonimizar</h3>
                <p>Elige qué tipos de datos personales quieres eliminar. Activa el modo recuperable si necesitas restaurarlos después.</p>
              </div>

              <div className="how-step">
                <div className="step-number">03</div>
                <div className="step-icon"><DownloadIcon className="text-primary" /></div>
                <h3>Descarga el resultado</h3>
                <p>Obtén tu documento anonimizado listo para usar. Si activaste el modo recuperable, guarda la clave de forma segura.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Privacy Section */}
        <section id="privacidad" className="privacy-section">
          <div className="container">
            <div className="privacy-header">
              <h2>Privacidad y seguridad</h2>
              <p>Tu información siempre bajo tu control</p>
            </div>

            <div className="privacy-grid">
              <div className="privacy-card">
                <div className="privacy-icon"><ServerIcon className="text-primary" /></div>
                <h3>Procesamiento local</h3>
                <p>Los documentos se procesan en el servidor y se eliminan inmediatamente después. No almacenamos ningún documento.</p>
              </div>

              <div className="privacy-card">
                <div className="privacy-icon"><LockIcon className="text-primary" /></div>
                <h3>Sin terceros</h3>
                <p>No utilizamos servicios de IA externos ni enviamos datos a terceros. Todo el procesamiento es interno.</p>
              </div>

              <div className="privacy-card">
                <div className="privacy-icon"><KeyIcon className="text-primary" /></div>
                <h3>Cifrado AES-256</h3>
                <p>El modo recuperable utiliza cifrado de grado militar para proteger el mapa de sustituciones.</p>
              </div>

              <div className="privacy-card">
                <div className="privacy-icon"><ShieldIcon className="text-primary" /></div>
                <h3>Cumplimiento RGPD</h3>
                <p>Herramienta diseñada para ayudar a los profesionales a cumplir con la normativa de protección de datos.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// ─── Main App with Router ───────────────────────────────────────────────────

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/aviso-legal" element={<AvisoLegal />} />
        <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
        <Route path="/politica-cookies" element={<PoliticaCookies />} />
        <Route path="/admin" element={<AdminPanel />} />
      </Routes>
      <CookieBanner />
    </BrowserRouter>
  );
}

export default App;
